// libs
import React, { useRef, memo, useState, useEffect, useCallback } from 'react';

// contexts
import WidgetContext from './WidgetContext';

// defs
interface IWidgetProviderProps {
  children: React.ReactNode;
}

const EMPTY_FUNC = () => {};

function WidgetProvider({ children }: IWidgetProviderProps) {
  let widgetEl = useRef<HTMLDivElement>(null);
  const [isScrollPromised, setIsScrollPromised] = useState(false);
  const [isRefSet, setIsRefSet] = useState(false);
  const onScrollCallback = useRef(EMPTY_FUNC);

  useEffect(() => {
    let handle: number;
    if (isScrollPromised && isRefSet) {
      handle = window.requestAnimationFrame(() => {
        if (widgetEl.current !== null) {
          widgetEl.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
          setIsScrollPromised(false);
        }
      });
    }

    return () => {
      if (handle !== undefined) {
        window.cancelAnimationFrame(handle);
      }
    };
  }, [isRefSet, isScrollPromised]);

  const setWidgetEl = useCallback((newWidgetEl: React.RefObject<HTMLDivElement>) => {
    widgetEl.current = newWidgetEl.current;
    setIsRefSet(true);
  }, []);

  const setOnScrollCallback = useCallback((cb: VoidFunction) => {
    onScrollCallback.current = cb;
  }, []);

  const scrollToWidget = useCallback(() => {
    onScrollCallback.current();
    setIsScrollPromised(true);
  }, []);

  return (
    <WidgetContext.Provider value={{ setWidgetEl, scrollToWidget, setOnScrollCallback }}>
      {children}
    </WidgetContext.Provider>
  );
}

export default memo(WidgetProvider);
